import { useCallback, useRef, useState } from 'react'

import { notifyMethod } from '../../../App'
import useRedux from '../../../hooks/useRedux'
import useTranslations from '../../../hooks/useTranslations'
import { nameParam } from '../../../utils'
import { userWiseRole } from '../../../utils/constant'
import debounce from '../../../utils/debounce'
import {
  entries,
  include,
  isEqual,
  notEqual,
  ternary,
} from '../../../utils/javascript'
import {
  getUserProfileApi,
  getUserRemindersApi,
  searchUserApi,
} from '../user.api'
import { userRelationKey, userSearchByOptions } from '../user.description'

const getProfileDetails = response => response?.data?.data || response?.data
const getReminderEmails = userDetails =>
  Object.entries(userDetails || {})
    .filter(([key, value]) => key.includes('emailId') && value)
    .map(([, value]) => value)

const userTable = ({
  payload,
  multiSelect,
  searchByEmail,
  omitSearchRelationType,
  searchParamName,
}) => {
  const { t } = useTranslations()
  const [viewModel, setViewModel] = useState({ open: false, userDetails: null })
  const searchValue = useRef(null)
  const [searchResult, setSearchResult] = useState({
    loader: false,
    data: null,
  })
  const [selectedUsers, setSelectedUsers] = useState([])
  const { selector } = useRedux()
  const isDesktop = selector(state => state.app.isDesktop)
  const [editInfo, setEditInfo] = useState({ flag: false, data: {} })
  const [reminderModel, setReminderModel] = useState({
    open: false,
    userDetails: {},
  })
  const [reminderLoader, setReminderLoader] = useState(false)
  const [previousReminderModel, setPreviousReminderModel] = useState({
    open: false,
    userDetails: {},
  })
  const [previousReminderData, setPreviousReminderData] = useState({
    loader: false,
    list: [],
    pageNo: 1,
    lastPage: 1,
    total: 0,
  })
  const [searchBy, setSearchBy] = useState({
    value: 'businessName',
    placeHolder: 'user_BusinessName',
  })
  const { dealer, collectionCenter } = userWiseRole

  const handleView = data => {
    setViewModel({ open: true, userDetails: data })
  }

  const onSearch = debounce(async e => {
    const { value } = e.target
    if (!value?.trim()) return setSearchResult({ loader: false, data: null })
    searchValue.current = value
    apiCall({ pageNo: 1, value })
  }, 500)

  const apiCall = useCallback(
    async ({ pageNo, value }) => {
      setSearchResult(pre => ({ ...pre, loader: true }))
      let params = `${pageNo}?${
        include([dealer, collectionCenter], payload?.roleId)
          ? searchBy?.value
          : searchParamName ||
            nameParam({
              roleId: payload?.roleId,
              searchByEmail,
            })
      }=${value}`
      entries(payload)?.forEach(([key, value]) => {
        if (value) {
          params += `&${key}=${value}`
        }
      })
      if (!payload.relationType && !omitSearchRelationType) {
        params += `&relationType=${userRelationKey.associate}`
      }
      const result = await searchUserApi({ params })
      setSearchResult({ loader: false, data: result?.data })
    },
    [omitSearchRelationType, searchBy, searchParamName],
  )

  const handleSearchTableChange = pagination => {
    apiCall({ pageNo: pagination.current, value: searchValue.current })
  }

  const handleCancel = () => {
    setViewModel({ open: false, userDetails: null })
  }

  const handleSelectChange = e => {
    const { checked, value } = e.target
    let tempList = [...selectedUsers]
    if (checked) {
      tempList = ternary(multiSelect, [...tempList, value], [value])
    } else {
      tempList = tempList.filter(v => notEqual(v?.id, value?.id))
    }
    setSelectedUsers(tempList)
  }

  const handleEdit = rowData => {
    setEditInfo({ flag: true, data: rowData })
  }
  const handleCancelEdit = () => {
    setEditInfo({ flag: false, data: {} })
  }

  const handleSendReminder = async rowData => {
    const rowEmails = getReminderEmails(rowData)
    if (rowEmails.length) {
      setReminderModel({ open: true, userDetails: rowData })
    }

    if (rowData?.id) {
      const response = await getUserProfileApi({ id: rowData?.id })
      const userDetails = { ...rowData, ...getProfileDetails(response) }
      const reminderEmails = getReminderEmails(userDetails)

      if (!reminderEmails.length) {
        setReminderModel({ open: false, userDetails: {} })
        notifyMethod.warning({ message: 'user_reminderEmailMissing' })
        return
      }

      setReminderModel({
        open: true,
        userDetails,
      })
    } else if (!rowEmails.length) {
      notifyMethod.warning({ message: 'user_reminderEmailMissing' })
    }
  }

  const handleCancelReminder = () => {
    setReminderModel({ open: false, userDetails: {} })
  }

  const fetchPreviousReminders = useCallback(async ({ userId, pageNo = 1 }) => {
    if (!userId) return
    setPreviousReminderData(prev => ({ ...prev, loader: true }))
    const response = await getUserRemindersApi({ pageNo, userId })
    const data = response?.data || response || {}
    const list = data?.list || []
    const currentPage = data?.pageNo
    const lastPage = data?.lastPage
    const total =
      data?.total || data?.totalCount || data?.totalElements || lastPage * 10

    setPreviousReminderData({
      loader: false,
      list,
      pageNo: currentPage,
      lastPage,
      total,
    })
  }, [])

  const handlePreviousReminders = rowData => {
    setPreviousReminderModel({ open: true, userDetails: rowData })
    fetchPreviousReminders({ userId: rowData?.id, pageNo: 1 })
  }

  const handlePreviousReminderTableChange = pagination => {
    fetchPreviousReminders({
      userId: previousReminderModel?.userDetails?.id,
      pageNo: pagination?.current || 1,
    })
  }

  const handleCancelPreviousReminders = () => {
    setPreviousReminderModel({ open: false, userDetails: {} })
    setPreviousReminderData({
      loader: false,
      list: [],
      pageNo: 1,
      lastPage: 1,
      total: 0,
    })
  }

  const searchByOptions = userSearchByOptions.map(({ label, value }) => ({
    label: t(label),
    value: value,
  }))

  const handleSearchByChange = value => {
    const searchIndex = userSearchByOptions.findIndex(opt =>
      isEqual(opt?.value, value),
    )
    setSearchBy({
      value,
      placeHolder: userSearchByOptions?.[searchIndex]?.label,
    })
  }

  const searchByProps = {
    value: searchBy,
    options: searchByOptions,
    onChange: handleSearchByChange,
  }
  return {
    searchValue,
    viewModel,
    isDesktop,
    searchResult,
    onSearch,
    handleView,
    handleSearchTableChange,
    handleCancel,
    selectedUsers,
    handleSelectChange,
    handleEdit,
    handleCancelEdit,
    editInfo,
    setEditInfo,
    searchByProps,
    reminderModel,
    reminderLoader,
    handleSendReminder,
    handleCancelReminder,
    setReminderLoader,
    previousReminderModel,
    previousReminderData,
    handlePreviousReminders,
    handlePreviousReminderTableChange,
    handleCancelPreviousReminders,
  }
}

export default userTable
